import React, { useEffect, useState } from "react";
import { Table, Tag, Button, Input, Pagination, Avatar, Spin, Tooltip, message, Popconfirm, Switch } from "antd";
import EditableImage from "../../../../components/icons/EditIcon";
import {
    LeftOutlined,
    SearchOutlined,
    FilterOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    UserOutlined,
    DeleteFilled,
    DeleteTwoTone
} from "@ant-design/icons";
import axiosInstance from "../../../../api/Axios";

const UploadDetails = ({ current, setCurrent, uploadId, id }) => {
    const [data, setData] = useState([]);
    const [counts, setCounts] = useState({
        added: 0,
        valid: 0,
        error: 0,
        total_records: 0
    });
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [uploadInfo, setUploadInfo] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // Inline Edit State
    const [editingId, setEditingId] = useState(null);
    const [editingValues, setEditingValues] = useState({});

    const handleEdit = (record) => {
        setEditingId(record.id);
        setEditingValues({
            user_id: record.user_id,
            first_name: record.first_name,
            last_name: record.last_name,
            email: record.email,
            phone_number: record.phone_number,
            access: record.access,
            level1: record.level1,
            level2: record.level2,
            level3: record.level3,
            location: record.location,
            organization: id
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingValues({});
    };

    const handleInputChange = (field, value) => {
        setEditingValues(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async (record) => {
        setLoading(true);
        try {
            const csrfToken = getCookie("csrftoken");

            // Restricted payload as requested
            const payload = {
                user_id: editingValues.user_id,
                first_name: editingValues.first_name,
                last_name: editingValues.last_name,
                email: editingValues.email,
                phone_number: editingValues.phone_number,
                access: editingValues.access,
                level1: editingValues.level1,
                level2: editingValues.level2,
                level3: editingValues.level3,
                location: editingValues.location,
                organization: id
            };

            // 1. PUT request for updation
            await axiosInstance.put(`customer/userupload/?id=${record.id}`, payload, {
                headers: {
                    "X-CSRFToken": csrfToken,
                }
            });

            // 2. POST request for usercheck
            await axiosInstance.post(`customer/usercheck/`, payload, {
                headers: {
                    "X-CSRFToken": csrfToken,
                }
            });

            message.success("Record updated and validated successfully");
            fetchData(pagination.current, pagination.pageSize);

            setEditingId(null);
            setEditingValues({});
        } catch (error) {
            console.error("Error saving/validating record:", error);
            message.error("Failed to update or validate record.");
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            // API Call: customer/userupload/?organization_id=...&page=...&upload_id=...
            const response = await axiosInstance.get(
                `customer/useruploadlist/?organization_id=${id}&page=${page}&page_size=${pageSize}&upload_id=${uploadId}`
            );

            if (response.data) {
                const results = response.data.results || [];
                setData(results);
                setPagination({
                    ...pagination,
                    current: page,
                    pageSize: pageSize,
                    total: response.data.count || 0
                });

                if (results.length > 0) {
                    const firstRecord = results[0];
                    setUploadInfo({
                        upload_id: firstRecord.upload, // ID from the upload object or direct field
                        // The JSON sample shows "upload": 383 (int). The screenshot shows "UP00000003".
                        // Use a formatter if needed, or raw.
                        uploaded_by: firstRecord.created_by
                    });
                }

                const validCount = results.filter(r => r.state === 'Valid').length;
                const errorCount = results.filter(r => r.state === 'Error').length;

                setCounts({
                    added: response.data.count, // Total Added ?
                    valid: validCount, // displaying only for current page? Or maybe I assume count for now.
                    error: errorCount,
                    total_records: response.data.count
                });
            }
        } catch (error) {
            console.error("Error fetching upload details:", error);
            message.error("Failed to load upload details.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (uploadId) {
            fetchData(pagination.current, pagination.pageSize);
        }
    }, [uploadId]);

    const handlePageChange = (page, pageSize) => {
        fetchData(page, pageSize);
    };

    const renderCellWithError = (text, record, field) => {
        const error = record.errors && record.errors[field];
        if (error) {
            return (
                <Tooltip title={error}>
                    <div style={{
                        border: '1px solid #ff4d4f',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        background: '#fff1f0',
                        cursor: 'help'
                    }}>
                        {text || '(Empty)'}
                    </div>
                </Tooltip>
            );
        }
        return <div style={{ padding: '4px 8px' }}>{text}</div>;
    };

    const columns = [
        {
            title: 'USER ID',
            dataIndex: 'user_id',
            key: 'user_id',
            render: (text, record) => {
                const isEditing = record.id === editingId;
                return isEditing ? (
                    <Input
                        value={editingValues.user_id}
                        onChange={(e) => handleInputChange('user_id', e.target.value)}
                        size="small"
                    />
                ) : (
                    <span style={{ color: '#1890ff' }}>{text}</span>
                );
            }
        },
        {
            title: 'FIRST NAME',
            dataIndex: 'first_name',
            key: 'first_name',
            render: (text, record) => {
                const isEditing = record.id === editingId;
                return isEditing ? (
                    <Input
                        value={editingValues.first_name}
                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                        size="small"
                    />
                ) : renderCellWithError(text, record, 'first_name');
            }
        },
        {
            title: 'LAST NAME',
            dataIndex: 'last_name',
            key: 'last_name',
            render: (text, record) => {
                const isEditing = record.id === editingId;
                return isEditing ? (
                    <Input
                        value={editingValues.last_name}
                        onChange={(e) => handleInputChange('last_name', e.target.value)}
                        size="small"
                    />
                ) : renderCellWithError(text, record, 'last_name');
            }
        },
        {
            title: 'EMAIL ID',
            dataIndex: 'email',
            key: 'email',
            width: 200,
            render: (text, record) => {
                const isEditing = record.id === editingId;
                return isEditing ? (
                    <Input
                        value={editingValues.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        size="small"
                    />
                ) : renderCellWithError(text, record, 'email');
            }
        },
        {
            title: 'PHONE',
            dataIndex: 'phone_number',
            key: 'phone_number',
            width: 150,
            render: (text, record) => {
                const isEditing = record.id === editingId;
                return isEditing ? (
                    <Input
                        value={editingValues.phone_number}
                        onChange={(e) => handleInputChange('phone_number', e.target.value)}
                        size="small"
                    />
                ) : renderCellWithError(text, record, 'phone_number');
            }
        },
        {
            title: 'ACCESS',
            dataIndex: 'access',
            key: 'access',
            align: 'center',
            render: (access, record) => {
                const isEditing = record.id === editingId;
                return isEditing ? (
                    <Switch
                        checked={editingValues.access}
                        onChange={(checked) => handleInputChange('access', checked)}
                        size="small"
                    />
                ) : (
                    access ? <CheckCircleFilled style={{ color: '#52c41a', fontSize: '16px' }} />
                        : <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: '16px' }} />
                );
            }
        },
        {
            title: 'LEVEL 1',
            dataIndex: 'level1',
            key: 'level1',
            render: (text, record) => {
                const isEditing = record.id === editingId;
                return isEditing ? (
                    <Input
                        value={editingValues.level1}
                        onChange={(e) => handleInputChange('level1', e.target.value)}
                        size="small"
                    />
                ) : renderCellWithError(text, record, 'level1');
            }
        },
        {
            title: 'LEVEL 2',
            dataIndex: 'level2',
            key: 'level2',
            render: (text, record) => {
                const isEditing = record.id === editingId;
                return isEditing ? (
                    <Input
                        value={editingValues.level2}
                        onChange={(e) => handleInputChange('level2', e.target.value)}
                        size="small"
                    />
                ) : renderCellWithError(text, record, 'level2');
            }
        },
        {
            title: 'LEVEL 3',
            dataIndex: 'level3',
            key: 'level3',
            render: (text, record) => {
                const isEditing = record.id === editingId;
                return isEditing ? (
                    <Input
                        value={editingValues.level3}
                        onChange={(e) => handleInputChange('level3', e.target.value)}
                        size="small"
                    />
                ) : renderCellWithError(text, record, 'level3');
            }
        },
        {
            title: 'LOCATION',
            dataIndex: 'location',
            key: 'location',
            render: (text, record) => {
                const isEditing = record.id === editingId;
                return isEditing ? (
                    <Input
                        value={editingValues.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        size="small"
                    />
                ) : renderCellWithError(text, record, 'location');
            }
        },
        {
            title: 'STATUS',
            dataIndex: 'state',
            key: 'state',
            align: 'center',
            fixed: 'right',
            width: 100,
            render: (state, record) => {
                const isError = state === 'Error';
                const color = isError ? '#ff4d4f' : '#52c41a'; // Red or Green
                const text = state || 'Valid';

                const errors = record.errors || {};
                const errorMsg = Object.values(errors).join(', ') || 'Unknown Error';

                return (
                    <Tooltip title={isError ? errorMsg : null}>
                        <Tag color={color} style={{ minWidth: 60, textAlign: 'center', borderRadius: 4, border: 'none', color: 'white', fontSize: 12 }}>
                            {text}
                        </Tag>
                    </Tooltip>
                );
            }
        },
        {
            title: 'ACTIONS',
            key: 'actions',
            align: 'center',
            fixed: 'right',
            width: 120,
            render: (_, record) => {
                const isEditing = record.id === editingId;
                return isEditing ? (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                        <Tooltip title="Save">
                            <CheckCircleFilled
                                onClick={() => handleSave(record)}
                                style={{ color: '#52c41a', fontSize: '18px', cursor: 'pointer' }}
                            />
                        </Tooltip>
                        <Tooltip title="Cancel">
                            <CloseCircleFilled
                                onClick={handleCancelEdit}
                                style={{ color: '#ff4d4f', fontSize: '18px', cursor: 'pointer' }}
                            />
                        </Tooltip>
                    </div>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <EditableImage
                            handleRowClick={() => handleEdit(record)}
                            recordId={record}
                            passSize="small"
                        />
                        <Popconfirm
                            title="Delete User"
                            description="Are you sure delete this record?"
                            onConfirm={() => handleDeleteRow(record.id)}
                            okText="Yes"
                            cancelText="No"
                            placement="leftTop"
                        >
                            <DeleteTwoTone
                                style={{
                                    cursor: "pointer",
                                    fontSize: "1.6rem",
                                    marginLeft: "0.7rem",
                                }}
                                title="Archive"
                                twoToneColor="#eb2f96"
                            />
                        </Popconfirm>
                    </div>
                );
            }
        }
    ];

    // Get CSRF token function
    function getCookie(name) {
        const cookies = document.cookie.split("; ");
        for (let i = 0; i < cookies.length; i++) {
            const [cookieName, cookieValue] = cookies[i].split("=");
            if (cookieName === name) {
                return cookieValue;
            }
        }
        return null;
    }

    const handleDeleteRow = async (recordId) => {
        setLoading(true);
        try {
            const csrfToken = getCookie("csrftoken");
            await axiosInstance.delete(`customer/userupload/?ids=${recordId}`, {
                headers: {
                    "X-CSRFToken": csrfToken,
                }
            });

            message.success("Record deleted successfully");
            fetchData(pagination.current, pagination.pageSize);
        } catch (error) {
            console.error("Error deleting record:", error);
            message.error("Failed to delete record.");
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (!selectedRowKeys.length) return;

        setLoading(true);
        try {
            const csrfToken = getCookie("csrftoken");
            const response = await axiosInstance.post(
                'customer/userupload/',
                { userupload_ids: selectedRowKeys },
                {
                    headers: {
                        "X-CSRFToken": csrfToken,
                    }
                }
            );

            if (response.data) { // Assuming success if data is returned, detailed check if needed
                message.success("Records added successfully");
                setSelectedRowKeys([]); // Clear selection
                if (uploadId) { // Refresh data
                    fetchData(pagination.current, pagination.pageSize);
                }
            }
        } catch (error) {
            console.error("Error adding records:", error);
            message.error("Failed to add records.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '10px', overflowX: 'hidden' }}>


            {/* Summary Card */}
            <div style={{ background: '#fff', border: '1px solid #e8e8e8', marginBottom: 20, display: 'flex', overflowX: 'auto' }}>
                {/* Upload ID Section */}
                <div style={{ padding: '16px 24px', borderRight: '1px solid #f0f0f0', minWidth: 150 }}>
                    <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4 }}>UPLOAD ID</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#333' }}>
                        {/* Display generic ID if text, or construct UP000... if numeric */}
                        {uploadInfo?.upload_id ? `UP${String(uploadInfo.upload_id).padStart(8, '0')}` : 'UP00000000'}
                    </div>
                </div>

                {/* Uploaded By Section */}
                <div style={{ padding: '16px 24px', borderRight: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 12, minWidth: 220 }}>
                    <Avatar size={40} style={{ backgroundColor: '#d9d9d9' }} icon={<UserOutlined />} />
                    <div>
                        <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>UPLOADED BY</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
                            {uploadInfo?.uploaded_by?.full_name || 'System Admin'}
                        </div>
                    </div>
                </div>

                {/* Record No Section (from screenshot, looks like "RECORD NO 1"?) */}
                <div style={{ padding: '16px 24px', borderRight: '1px solid #f0f0f0', minWidth: 120 }}>
                    <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4 }}>RECORD NO</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#333' }}>
                        1 {/* Placeholder as per screenshot */}
                    </div>
                </div>

                {/* Stats Section */}
                <div style={{ padding: '16px 24px', display: 'flex', gap: 32, alignItems: 'center', flex: 1, minWidth: 300 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontSize: 24, fontWeight: 400, color: '#faad14' }}>{counts.added}</span>
                        <span style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>Added</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontSize: 24, fontWeight: 400, color: '#52c41a' }}>{counts.valid}</span>
                        <span style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>Valid</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontSize: 24, fontWeight: 400, color: '#ff4d4f' }}>{counts.error}</span>
                        <span style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>Error</span>
                    </div>
                </div>
            </div>



            {/* Bulk Actions Bar */}
            {selectedRowKeys.length > 0 && (
                <div style={{
                    marginBottom: 16,
                    padding: '8px 16px',
                    background: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16
                }}>
                    <span style={{ fontWeight: 600 }}>{selectedRowKeys.length} Selected</span>
                    <Button type="primary" size="small" onClick={handleAdd}>
                        Add
                    </Button>
                    <Button danger size="small" onClick={() => message.info('Delete action triggered')}>
                        Delete
                    </Button>
                </div>
            )}

            {/* Data Table */}
            <Table
                rowSelection={{
                    selectedRowKeys,
                    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
                    getCheckboxProps: (record) => ({
                        disabled: record.state === 'Error',
                        name: record.name,
                    }),
                }}
                columns={columns}
                dataSource={data}
                loading={loading}
                rowKey="id"
                size="small"
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onChange: handlePageChange,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '30', '50'],
                    position: ['bottomRight']
                }}
                bordered
                scroll={{ x: 1300 }}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    background: '#ccc',
                                    color: '#333',
                                    fontWeight: 600,
                                    height: 48,
                                    borderBottom: 'none',
                                    ...props.style
                                }}
                            />
                        )
                    }
                }}
            />
        </div>
    );
};

export default UploadDetails;
